import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IconifyIcon from '../../../components/IconifyIcon';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button } from 'react-bootstrap';
import familyRepository from '../../../services/repositories/family.repository';
import '../../../styles/products.scss';

const AddFamily = ({ isEdit = false, familyId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isEdit && familyId) {
      fetchFamilyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, familyId]);

  const fetchFamilyData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await familyRepository.getById(familyId);
      const family = response.data || response;
      
      if (!family || !family.id) {
        throw new Error('Family data not found');
      }
      
      setFormData({
        name: family.name || '',
        description: family.description || '',
      });
    } catch (err) {
      console.error('Error fetching family:', err);
      setError(err.message || 'Error loading family data');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const submitData = {
        name: formData.name || '',
        description: formData.description || '',
      };
      
      if (isEdit && familyId) {
        await familyRepository.update(familyId, submitData);
      } else {
        await familyRepository.create(submitData);
      }
      
      navigate('/admin/families/family-list');
    } catch (err) {
      console.error('Error saving family:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde de la famille');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/families/family-list');
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données de la famille...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>
            {isEdit ? 'Modifier la Famille' : 'Nouvelle Famille'}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="family-name" className="form-label">
                    Nom de la Famille <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="family-name"
                    name="name"
                    className="form-control"
                    placeholder="Nom de la famille"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="family-description" className="form-label">
                    Description
                  </label>
                  <textarea
                    className="form-control bg-light-subtle"
                    id="family-description"
                    name="description"
                    rows={5}
                    placeholder="Description de la famille"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
            
            <div className="p-3 bg-light mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button 
                    variant="dark" 
                    className="w-100" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button 
                    variant="primary" 
                    className="w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer la Famille')}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AddFamily;
